import { isArray, get } from 'lodash-es';
import debug from 'debug';
import * as z from 'zod';
import * as ba from './base';
import { SCRIPT_SCHEMA } from '../scripting';
import { assertComponentContext } from '../helpers';
import {
  Resource,
  ResourceEditor,
  ResourceContainer,
  isResourceEditor,
} from '../resource';
import { AxiosResponse, Method } from 'axios';

const log = debug('itp:core:actions:rest');

export interface RestActionArgs {
  method: 'save' | 'delete';
  container?: ResourceContainer;
  resource: Resource | ResourceEditor;
  data?: any;
}

export class RestAction extends ba.Action<RestActionArgs, any> {
  static get manifest() {
    return {
      name: 'rest',
      schema: z.object({
        method: z.union([z.literal('save'), z.literal('delete')]),
        container: z.union([SCRIPT_SCHEMA(), z.record(z.string(),z.any())]).optional(),
        resource: z.union([SCRIPT_SCHEMA(), z.record(z.string(),z.any())]),
        data: z.union([SCRIPT_SCHEMA(), z.record(z.string(),z.any())]).optional(),
      }),
    };
  }

  async handle(event: ba.ActionEvent, args: RestActionArgs) {
    assertComponentContext(event.component);
    const conn = event.component.$app.connections.default;
    const headers: Dictionary<string> = {};
    const isDelete = args.method === 'delete';

    let method: Method = isDelete ? 'delete' : 'post';
    let url = args.container?.self;
    let data = args.data || args.resource.getData();
    const resource = args.resource;

    if (!resource.isNew) {
      if (!isDelete) {
        method = 'patch';
        data = args.data || resource.getChanges();
      }
      url = resource.getURL();
      headers['If-Match'] = resource.getETAG();
    }

    log(
      'REST action: method=%s; url=%s; data=%o; headers=%o; resource=%o',
      method,
      url,
      data,
      headers,
      resource,
    );

    await this.dispatch('before', { resource, url, method, headers, data });

    const response = await conn.ajax({ url, method, headers, data });

    if (!isDelete && response.status === 201) {
      return await this.onCreated(response, args);
    }
    if (!isDelete && response.status === 200) {
      return await this.onUpdated(response, args);
    }
    if (isDelete) {
      return await this.onDeleted(response, args);
    }
  }

  private async onCreated(response: AxiosResponse, args: RestActionArgs) {
    const resource = resetData(response, args);
    if (args.container && isArray(args.container.data)) {
      args.container.data.push(resource);
    }
    await this.dispatch('created', { response, resource });
    await this.dispatch('saved', { response, resource });
    return response;
  }

  private async onUpdated(response: AxiosResponse, args: RestActionArgs) {
    const resource = resetData(response, args);
    await this.dispatch('updated', { response, resource });
    await this.dispatch('saved', { response, resource });
    return response;
  }

  /**
   * Removes the item from its container.
   */
  private async onDeleted(response: AxiosResponse, args: RestActionArgs) {
    const resourceID = args.resource.id;
    if (args.container && resourceID != null && isArray(args.container.data)) {
      const cdata = args.container.data;
      cdata.forEach((item, index) => {
        if (get(item, 'id') == resourceID) {
          cdata.splice(index, 1);
        }
      });
    }

    const resource = getRealResourceFromArgs(args);

    await this.dispatch('deleted', { response, resource });
    return response;
  }
}

function resetData(response: AxiosResponse, args: RestActionArgs) {
  const target = getRealResourceFromArgs(args);
  target.reset(response.data);
  return target;
}

function getRealResourceFromArgs(args: RestActionArgs) {
  return isResourceEditor(args.resource)
    ? args.resource.$_resource
    : args.resource;
}
